export type FieldType = "text" | "string-list" | "select" | "date" | "month-year" | "bool" | "skill";

export interface FieldOption {
  value: string;
  label: string;
  section?: string;
  /** Optional one-char prefix rendered before the label (e.g. "★" for rollup entries). */
  glyph?: string;
}

export interface FieldDef {
  key: string;
  label: string;
  type: FieldType;
  options?: FieldOption[];
  /** Uses the catalogue-specific grouped/searchable degree control. */
  selectKind?: "degree";
  placeholder?: string;
  required?: boolean;
  hideLabel?: boolean;
  prominent?: boolean;
  /**
   * Optional DOM id stamped on this field's input by EntryFields — a stable,
   * locale-independent hook for e2e tests (spark-validation), mirroring
   * `Select.id`. month-year fields derive `${id}-month` / `${id}-year`.
   */
  id?: string;
  /**
   * Opts a `string-list` field into AI-backed suggestions. "activity" wires
   * the field to `suggestActivityDescriptions`, driven by the sibling
   * `jobTitle` / `department` values of the same entry.
   */
  suggest?: "activity";
  escoKind?: "occupation" | "skill";
  /**
   * When true, EntryFields renders this field and the immediately-following
   * field side by side in a 2-column grid at `sm:` breakpoint and up. Both
   * paired fields should be roughly short and same-type for the layout to
   * read cleanly (e.g. Arbeitgeber + Abteilung, Land + Ort).
   */
  pairWithNext?: boolean;
  /**
   * Conditionally required based on sibling values in the same entry (e.g. a
   * foreign language needs a CEFR level, a native speaker doesn't). Evaluated
   * by `isFieldMissingInEntry`; the value-only `isFieldMissing` ignores it.
   */
  requiredWhen?: (entry: Record<string, unknown>) => boolean;
  /**
   * Ceiling on the number of entries a `string-list` field may hold. Declared here rather than read
   * from a page module so components/design stays free of page imports. Drives the visible counter and
   * disables the add affordances at the cap.
   */
  maxEntries?: number;
}

// A field is required only when explicitly marked. Default = optional.
export const isRequired = (f: FieldDef): boolean => f.required === true;

const MONTH_YEAR_SHAPE = /^\d{4}(-\d{2}(-\d{2})?)?$/;

/**
 * A month-year field holding a value that is present but not a usable date — the `-MM` sentinel
 * MonthYearSelect writes when a month is picked without a year.
 *
 * This is NOT about required-ness, which is why it is separate: on an OPTIONAL date the sentinel used
 * to sail through every gate, `toDateInstant` turned it into null, and the mutation cleared the field.
 * So the user's pick was discarded — and if the row already had a date and they only cleared the year,
 * the saved date was wiped, with the save reporting success.
 */
export function isFieldIncomplete(f: FieldDef, value: unknown): boolean {
  if (f.type !== "month-year") return false;
  const s = String(value ?? "").trim();
  return s !== "" && !MONTH_YEAR_SHAPE.test(s);
}

// Empty-or-unusable check for a field's value, independent of whether it's required. For a month-year
// field the `-MM` sentinel counts as empty here too, so a REQUIRED date still shows its PFLICHTFELD
// tooltip; isFieldIncomplete is what extends the same rejection to optional dates.
function isEmptyValue(f: FieldDef, value: unknown): boolean {
  if (value == null) return true;
  if (typeof value === "boolean") return false;
  const s = String(value).trim();
  if (s === "") return true;
  return f.type === "month-year" && !MONTH_YEAR_SHAPE.test(s);
}

// Centralised "can this field's value not be committed?" predicate. Used by every form that gates a
// confirm button so the rules stay consistent across the edit panels (rows.tsx, CareerGraph.tsx,
// SkillsDiagram.tsx) and the add flow (AddForm.tsx). Two ways to fail: a required field left empty, or
// ANY month-year field left mid-way (see isFieldIncomplete — that one applies to optional fields too,
// because the alternative is silently clearing the date).
export function isFieldMissing(f: FieldDef, value: unknown): boolean {
  return isFieldIncomplete(f, value) || (isRequired(f) && isEmptyValue(f, value));
}

// Entry-aware variant: also honours `requiredWhen(entry)`, so a field can be
// required based on a sibling value (e.g. cefr required unless nativeSpeaker).
// Pass the whole entry/form so the predicate can read its siblings.
export function isFieldMissingInEntry(f: FieldDef, entry: Record<string, unknown>): boolean {
  const value = entry[f.key];
  if (isFieldIncomplete(f, value)) return true;
  const required = isRequired(f) || (f.requiredWhen?.(entry) ?? false);
  return required && isEmptyValue(f, value);
}
